/**
 * Theme-driven styling config for the booking widget (Phase 2).
 * Emitted as `data-styling-config` on `#react-product`.
 * Pathway keys: `hk` (HyperKarting) and `bk` (BattleKart).
 */

export type PathwayKey = "hk" | "bk";

export type ScreenStyleKey =
  | "trackSelection"
  | "ageGroupSelection"
  | "seatsRaces"
  | "playersSessions"
  | "postCode"
  | "dateTime"
  | "bookingSummary"
  | "addAnotherAttraction"
  | "arcadeCard"
  | "arcadeExperience"
  | "arcadeCart";

export interface TypographyStyle {
  fontSize?: string;
  fontWeight?: string | number;
  italic?: boolean;
  color?: string;
}

export interface ScreenStyle {
  /** Solid colour or CSS gradient for the screen surface */
  background?: string;
  title?: TypographyStyle;
  subtitle?: TypographyStyle;
}

export interface ProgressBarStyle {
  background?: string;
  borderRadius?: string;
  arrowRadius?: string;
  labelFontSize?: string;
  labelColor?: string;
}

export interface CtaStyle {
  background?: string;
  borderRadius?: string;
  fontSize?: string;
  paddingX?: string;
  paddingY?: string;
  color?: string;
}

export interface PreviousButtonStyle {
  /** Solid colour or CSS gradient for the button surface */
  background?: string;
  /** Colour of the arrow / icon */
  iconColor?: string;
  borderRadius?: string;
}

export interface StickySummaryStyle {
  background?: string;
  desktopAccent?: string;
  borderRadius?: string;
  pillBackground?: string;
  pillTextColor?: string;
  pillFontSize?: string;
  pillBorderRadius?: string;
  pillFontWeight?: string | number;
}

export interface ModalStyle {
  borderBackground?: string;
  contentBackground?: string;
  borderRadius?: string;
  titleColor?: string;
  bodyColor?: string;
  buttonBackground?: string;
  buttonBorderRadius?: string;
  buttonFontSize?: string;
}

export interface PathwayStyling {
  /** Default background applied across all screens for this pathway */
  screenBackground?: string;
  ambientDotA?: string;
  ambientDotB?: string;
  screens?: Partial<Record<ScreenStyleKey, ScreenStyle>>;
  progressBar?: ProgressBarStyle;
  cta?: CtaStyle;
  previousButton?: PreviousButtonStyle;
  stickySummary?: StickySummaryStyle;
  modals?: ModalStyle;
}

export interface VisibilityConfig {
  /** Flow / step screens */
  trackSelection?: boolean;
  racingTrackSelection?: boolean;
  postCode?: boolean;
  addAnotherAttraction?: boolean;
  bookingSummary?: boolean;
  /** Attraction tiles / routes */
  racingGames?: boolean;
  mainTrack?: boolean;
  miniTrack?: boolean;
  juniorTrack?: boolean;
  intermediateTrack?: boolean;
  battleKart?: boolean;
  arcade?: boolean;
  virtualReality?: boolean;
}

export interface TStylingConfig {
  visibility?: VisibilityConfig;
  pathways?: {
    hk?: PathwayStyling;
    bk?: PathwayStyling;
  };
}

/** Production defaults — mirror current hardcoded HK / BK visuals */
export const DEFAULT_STYLING_CONFIG: TStylingConfig = {
  visibility: {
    trackSelection: true,
    racingTrackSelection: true,
    postCode: true,
    addAnotherAttraction: true,
    bookingSummary: true,
    racingGames: true,
    mainTrack: true,
    miniTrack: true,
    juniorTrack: true,
    intermediateTrack: true,
    battleKart: true,
    arcade: true,
    virtualReality: true,
  },
  pathways: {
    hk: {
      screenBackground: "#000000",
      ambientDotA: "rgb(5, 255, 250)",
      ambientDotB: "rgb(249, 8, 255)",
      progressBar: {
        background: "linear-gradient(270deg, #F908FF 0%, #D32EFE 8.19%, #37CCFB 87.88%, #05FFFA 100%)",
        borderRadius: "9999px",
        arrowRadius: "4px",
        labelFontSize: "14px",
        labelColor: "#ffffff",
      },
      cta: {
        background: "linear-gradient(270deg, #F908FF 0%, #D32EFE 8.19%, #37CCFB 87.88%, #05FFFA 100%)",
        borderRadius: "9999px",
        fontSize: "14px",
        paddingX: "20px",
        paddingY: "8px",
        color: "#ffffff",
      },
      previousButton: {
        background: "#4A4C52",
        iconColor: "#ffffff",
        borderRadius: "9999px",
      },
      stickySummary: {
        background: "#393B41",
        desktopAccent: "#9d62fe",
        borderRadius: "15px",
        pillBackground: "linear-gradient(90deg, #00FFFF 0%, #FF00FF 100%)",
        pillTextColor: "#ffffff",
        pillFontSize: "13px",
        pillBorderRadius: "9999px",
        pillFontWeight: 600,
      },
      modals: {
        borderBackground: "linear-gradient(282.87deg, #d32efe 14.09%, #05fffa 103.26%)",
        contentBackground: "#000000",
        borderRadius: "16px",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        buttonBackground: "linear-gradient(282.87deg, #d32efe 14.09%, #05fffa 103.26%)",
        buttonBorderRadius: "100px",
        buttonFontSize: "16px",
      },
    },
    bk: {
      screenBackground: "#000000",
      ambientDotA: "rgb(250, 198, 24)",
      ambientDotB: "rgb(234, 88, 26)",
      progressBar: {
        background: "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)",
        borderRadius: "9999px",
        arrowRadius: "0px",
        labelFontSize: "14px",
        labelColor: "#ffffff",
      },
      cta: {
        background: "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)",
        borderRadius: "9999px",
        fontSize: "14px",
        paddingX: "20px",
        paddingY: "8px",
        color: "#ffffff",
      },
      previousButton: {
        background: "#4A4C52",
        iconColor: "#ffffff",
        borderRadius: "9999px",
      },
      stickySummary: {
        background: "#393B41",
        desktopAccent: "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)",
        borderRadius: "15px",
        pillBackground: "linear-gradient(0deg, #EA581A 0%, #FAC618 100%)",
        pillTextColor: "#ffffff",
        pillFontSize: "13px",
        pillBorderRadius: "9999px",
        pillFontWeight: 600,
      },
      modals: {
        borderBackground: "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)",
        contentBackground: "#000000",
        borderRadius: "16px",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        buttonBackground: "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)",
        buttonBorderRadius: "100px",
        buttonFontSize: "16px",
      },
    },
  },
};
